import { useState } from 'react';
import type { FormEvent } from 'react';
import { Modal } from '../../components/ui/Modal';
import { Field } from '../../components/ui/Field';
import { useToast } from '../../components/ui/Toast';
import { absenceMutations } from '../../lib/queries';
import { useFormErrors } from '../../lib/useFormErrors';
import { todayIso } from '../../lib/dates';
import { ABSENCE_LABELS, ABSENCE_STATUS_LABELS, HALF_DAY_LABELS } from '../../lib/labels';
import type { Absence, AbsenceStatus, AbsenceType, Employee, HalfDay } from '../../lib/types';

interface Props {
  absence: Absence | null;
  employees: Employee[];
  defaultEmployeeId?: number;
  /** Vorbelegung aus der Verfuegbarkeits-Matrix. */
  defaultRange?: { from: string; to: string };
  onClose: () => void;
}

export function AbsenceDialog({ absence, employees, defaultEmployeeId, defaultRange, onClose }: Props) {
  const isEdit = absence !== null;
  const toast = useToast();
  const { errors, capture, clear, fieldError } = useFormErrors();

  const [employeeId, setEmployeeId] = useState<number | ''>(
    absence?.employeeId ?? defaultEmployeeId ?? employees[0]?.id ?? '',
  );
  const [type, setType] = useState<AbsenceType>(absence?.type ?? 'urlaub');
  const [startDate, setStartDate] = useState(absence?.startDate ?? defaultRange?.from ?? todayIso());
  const [endDate, setEndDate] = useState(absence?.endDate ?? defaultRange?.to ?? todayIso());
  const [halfDay, setHalfDay] = useState<HalfDay>(absence?.halfDay ?? null);
  const [status, setStatus] = useState<AbsenceStatus>(absence?.status ?? 'genehmigt');
  const [note, setNote] = useState(absence?.note ?? '');

  const create = absenceMutations.useCreate();
  const update = absenceMutations.useUpdate();
  const busy = create.isPending || update.isPending;

  const singleDay = startDate === endDate;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    clear();
    if (employeeId === '') return;

    const payload = {
      employeeId,
      type,
      startDate,
      endDate,
      halfDay: singleDay ? halfDay : null,
      status,
      note,
    };

    const onSuccess = () => {
      toast.success(isEdit ? 'Eintrag gespeichert.' : `${ABSENCE_LABELS[type]} eingetragen.`);
      onClose();
    };

    if (isEdit) {
      update.mutate({ ...payload, id: absence.id }, { onSuccess, onError: capture });
    } else {
      create.mutate(payload, { onSuccess, onError: capture });
    }
  };

  return (
    <Modal
      title={isEdit ? 'Abwesenheit bearbeiten' : 'Abwesenheit eintragen'}
      subtitle="Urlaub, Krankheit, Frei und Sonstiges"
      onClose={onClose}
      width="voll"
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Abbrechen
          </button>
          <button
            type="submit"
            form="absence-form"
            className="btn btn-signal"
            disabled={busy || employeeId === ''}
          >
            {busy ? 'Wird gespeichert …' : 'Speichern'}
          </button>
        </>
      }
    >
      <form id="absence-form" className="stack" onSubmit={submit} noValidate>
        {errors.general ? (
          <p className="form-error" role="alert">
            {errors.general}
          </p>
        ) : null}

        {/* Großes festes Fenster mit Sinnblöcken — alle Felder ohne Scrollen
            auf einen Blick (Rückmeldung des Nutzers, 12.08.2026). */}
        <div className="form-grid">
          <span className="label form-section">Person</span>

          <Field label="Mitarbeiter" error={fieldError('employeeId')} className="span-2">
            {(props) => (
              <select
                {...props}
                className="select"
                value={employeeId}
                disabled={isEdit}
                onChange={(event) => setEmployeeId(Number(event.target.value))}
              >
                {employees.map((employee) => (
                  <option key={employee.id} value={employee.id}>
                    {employee.displayName}
                  </option>
                ))}
              </select>
            )}
          </Field>

          <span className="label form-section">Eintrag</span>

          <Field label="Art" error={fieldError('type')} className="span-2">
            {(props) => (
              <select
                {...props}
                className="select"
                value={type}
                onChange={(event) => setType(event.target.value as AbsenceType)}
              >
                {Object.entries(ABSENCE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            )}
          </Field>

          <Field
            label="Status"
            error={fieldError('status')}
            hint="Beantragte Einträge erscheinen im Plan schraffiert und zählen als vorgemerkt."
            className="span-2"
          >
            {(props) => (
              <select
                {...props}
                className="select"
                value={status}
                onChange={(event) => setStatus(event.target.value as AbsenceStatus)}
              >
                {Object.entries(ABSENCE_STATUS_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            )}
          </Field>

          <span className="label form-section">Zeitraum</span>

          <Field label="Von" error={fieldError('startDate')}>
            {(props) => (
              <input
                {...props}
                className="input"
                type="date"
                value={startDate}
                onChange={(event) => {
                  const value = event.target.value;
                  setStartDate(value);
                  if (value > endDate) setEndDate(value);
                }}
              />
            )}
          </Field>

          <Field label="Bis" error={fieldError('endDate')}>
            {(props) => (
              <input
                {...props}
                className="input"
                type="date"
                min={startDate}
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
              />
            )}
          </Field>

          {singleDay ? (
            <Field
              label="Halber Tag"
              error={fieldError('halfDay')}
              hint="Zählt mit 0,5 Tagen auf das Urlaubskonto."
              className="span-2"
            >
              {(props) => (
                <select
                  {...props}
                  className="select"
                  value={halfDay ?? ''}
                  onChange={(event) => setHalfDay((event.target.value || null) as HalfDay)}
                >
                  <option value="">Ganzer Tag</option>
                  {Object.entries(HALF_DAY_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          ) : null}

          <span className="label form-section">Notiz</span>

          <Field label="Notiz" error={fieldError('note')} className="span-2">
            {(props) => (
              <input
                {...props}
                className="input"
                value={note}
                onChange={(event) => setNote(event.target.value)}
              />
            )}
          </Field>
        </div>
      </form>
    </Modal>
  );
}
